import { useState, useEffect, useRef, useCallback } from 'react'

import {
  ApolloQueryResult,
  OperationVariables,
  useLazyQuery,
} from '@apollo/client'
import { isEqual } from 'lodash'

import { wildCardSearch, shouldExecuteQuery } from '@/utils/index'

/**
 * @param {DocumentNode} query the GraphQL query to fetch the data
 * @param {object} variables any variables required for the GraphQL query
 * @param {function} transformer a function to flatten the GraphQL result
 * @param {string} searchInput an optional search input to filter the data
 * @param {Function | null} setDataCallback an optional callback for transformed data
 * @param {Function | null} setQueryDataCallback an optional callback for query data
 */
export const useTableData = <QueryType, TransformedDataType>(
  query,
  variables,
  transformer: (data: QueryType) => TransformedDataType[],
  searchInput,
  setDataCallback: (data: TransformedDataType[]) => void = null,
  setQueryDataCallback: (data: QueryType) => void = null
): [
  boolean,
  TransformedDataType[],
  (
    variables?: Partial<OperationVariables>
  ) => Promise<ApolloQueryResult<QueryType>>,
] => {
  const hasFetchedData = useRef(false)
  const previousVariables = useRef<object>(variables)

  const [transformedData, setTransformedData] = useState<TransformedDataType[]>(
    []
  )
  const [filteredData, setfilteredData] = useState<TransformedDataType[]>([])

  const [getTableData, { loading }] = useLazyQuery<QueryType>(query, {
    fetchPolicy: 'cache-and-network',
  })

  // only fetch the data once all variables are valid, or if they've changed
  const shouldExecute = shouldExecuteQuery(query, variables)
  const variablesChanged = !isEqual(variables, previousVariables.current)

  const fetchTableData = useCallback(
    async (overrideVariables?) => {
      hasFetchedData.current = true
      previousVariables.current = variables

      const result = await getTableData({
        variables: overrideVariables ?? variables,
      })

      const { data } = result

      if (setQueryDataCallback) {
        setQueryDataCallback(data)
      }

      const transformedData = transformer(data)
      setTransformedData(transformedData)

      if (setDataCallback) {
        setDataCallback(transformedData)
      }

      return result
    },
    [
      getTableData,
      setDataCallback,
      setQueryDataCallback,
      transformer,
      variables,
    ]
  )

  useEffect(() => {
    if (shouldExecute && (!hasFetchedData.current || variablesChanged)) {
      fetchTableData()
    }
  }, [fetchTableData, shouldExecute, variablesChanged])

  // filter the original data when the search input changes
  useEffect(() => {
    if (searchInput && searchInput.length) {
      const filteredData = wildCardSearch(transformedData, searchInput)
      setfilteredData(filteredData)
    } else {
      setfilteredData(undefined)
    }
  }, [searchInput, transformedData])

  // use the filtered data if present
  const finalData = filteredData ? filteredData : transformedData

  return [loading, finalData, fetchTableData]
}
